/**
 * Pack Projects Section
 *
 * Lists projects linked to a feature pack with unlink action.
 * Uses ProjectSelector for adding projects (same UX as project config and workspaces).
 */

import { useState, type ReactElement } from 'react';
import {
  useDetachProjectFromPack,
  useLinkProjectToPack,
} from '../../hooks/useAdminPacks';
import type { LinkedProject } from '../../schemas/admin.schemas';
import { ProjectSelector } from '@features/workspace/components/ProjectSelector';
import { ServerSelect } from '@features/git/components/ServerSelect';
import { gitApi } from '@features/git/api/gitApi';
import type { GitProject } from '@features/git/types/git.types';

interface PackProjectsSectionProps {
  packId: string;
  linkedProjects: LinkedProject[];
}

export function PackProjectsSection({
  packId,
  linkedProjects,
}: PackProjectsSectionProps): ReactElement {
  const detachProject = useDetachProjectFromPack();
  const linkProject = useLinkProjectToPack();
  const [externalRef, setExternalRef] = useState('');
  const [serverId, setServerId] = useState<string | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [isLookingUp, setIsLookingUp] = useState(false);

  const handleUnlink = (projectId: string): void => {
    if (!window.confirm('Unlink this project from the pack?')) return;
    detachProject.mutate(projectId);
  };

  const handleSelect = (project: GitProject | null): void => {
    if (!project) return;
    linkProject.mutate({
      projectId: project.id,
      packId,
      projectName: project.path,
    });
  };

  const handleManualLookup = (): void => {
    if (!externalRef.trim() || !serverId) {
      setLookupError('Server and external ref are required');
      return;
    }
    setIsLookingUp(true);
    setLookupError(null);
    void (async (): Promise<void> => {
      try {
        const project = await gitApi.lookupProject(serverId, externalRef);
        linkProject.mutate({
          projectId: project.id,
          packId,
          projectName: project.path,
        });
        setExternalRef('');
      } catch (error) {
        setLookupError(
          error instanceof Error ? error.message : 'Failed to lookup project'
        );
      } finally {
        setIsLookingUp(false);
      }
    })();
  };

  return (
    <div className="audit-log">
      <h3 className="audit-log__title">Linked Projects</h3>
      {linkedProjects.length > 0 ? (
        <table className="feature-table__table">
          <thead>
            <tr>
              <th>Project</th>
              <th>Linked By</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {linkedProjects.map((lp) => (
              <tr key={lp.project_id}>
                <td>{lp.project_name}</td>
                <td>{lp.linked_by}</td>
                <td>
                  <button type="button" className="feature-table__edit-btn feature-table__edit-btn--danger"
                    onClick={() => { handleUnlink(lp.project_id); }}
                    disabled={detachProject.isPending}>
                    Unlink
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="audit-log__empty">No projects linked to this pack.</p>
      )}
      <div style={{ marginTop: '1rem', maxWidth: '400px' }}>
        <label style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>
          Add Project
        </label>
        <ProjectSelector
          selectedProject={null}
          onSelect={handleSelect}
          disabled={linkProject.isPending}
        />
      </div>
      <div style={{ marginTop: '1rem', maxWidth: '400px' }}>
        <label style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>
          Manual Project Lookup (by external ref)
        </label>
        <div style={{ marginBottom: '0.5rem' }}>
          <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>
            Git Server
          </label>
          <ServerSelect
            value={serverId}
            onChange={setServerId}
            requireCredential
          />
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            type="text"
            value={externalRef}
            onChange={(e) => { setExternalRef(e.target.value); }}
            placeholder="e.g. owner/repo or numeric id"
            className="form-select__search-input"
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="feature-table__edit-btn"
            onClick={handleManualLookup}
            disabled={!externalRef.trim() || !serverId || isLookingUp || linkProject.isPending}
          >
            {isLookingUp ? 'Looking up...' : 'Lookup'}
          </button>
        </div>
        {lookupError && (
          <p style={{ fontSize: '0.75rem', color: '#d32f2f', marginTop: '0.25rem' }}>
            Error: {lookupError}
          </p>
        )}
        <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
          Enter project identifier (e.g. numeric ID or path) to lookup and link.
        </p>
      </div>
    </div>
  );
}
